import { useRef, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import GameMenu from "../components/GameMenu";
import Timer from "../components/Timer";
import Popup from "../components/Popup";
import LeaveGamePopup from "../components/LeaveGamePopup";
import GameWrapper from "../components/GameCountdown";
import "../assets/css/vinyleQCM.css";
import api from "../api/client";

export default function VinyleQCM() {
  const [gameStarted, setGameStarted] = useState(false);

  const location = useLocation();
  const { theme, subTheme, level = "B1" } = location.state || {};

  const [timerIsFinished, setTimerIsFinished] = useState(false);
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [leaveGamePopup, setLeaveGamePopup] = useState(false);
  const getTimerTimeRef = useRef(null);

  // ── Chargement depuis l'API ──────────────────────────────────────
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState(null);

  const [translations, setTranslations] = useState({});
  const [hoveredAnswer, setHoveredAnswer] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams({ level, n: 10 });
    if (theme) params.append("theme", theme);
    if (subTheme) params.append("sous_theme", subTheme);

    api.get(`/api/games/mcq/?${params}`)
      .then(({ data }) => {
        const formatted = data.items.map((item) => ({
          question: item.question,
          options: item.choices,
          correctAnswer: item.answer,
        }));
        setQuestions(formatted);
        setLoading(false);
      })
      .catch((err) => {
        setApiError(err.message);
        setLoading(false);
      });
  }, [theme, subTheme, level]);

  useEffect(() => {
    if (!questions.length) return;
    const fetchTranslations = async () => {
      try {
        // récupérer tous les mots uniques
        const allWords = [
          ...new Set(
            questions.flatMap(q => q.options)
          )
        ];
        const results = await Promise.all(
          allWords.map(word =>
            api.get(`/api/lexicon/word/?term=${encodeURIComponent(word)}`)
              .then(res => ({ word, data: res.data }))
              .catch(() => ({ word, data: null }))
          )
        );
        const translationsMap = {};
        results.forEach(({ word, data }) => {
          translationsMap[word] = data?.results?.[0]?.translation_fr || null;
        });
        setTranslations(translationsMap);
      } catch (err) {
        console.error("Erreur traduction :", err);
      }
    };
    fetchTranslations();
  }, [questions]);
  // ────────────────────────────────────────────────────────────────

  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [score, setScore] = useState(0);
  const [finished, setFinished] = useState(false);

  const currentQuestion = questions[currentIndex];

  const sendTime = (time) => setTimeElapsed(time);
  const sendLeaveGame = (value) => setLeaveGamePopup(value);

  const handleAnswerClick = (answer) => {
    if (!selectedAnswer) {
      setSelectedAnswer(answer);
      if (answer === currentQuestion.correctAnswer) {
        setScore((prev) => prev + 1);
      }
    }
  };

  const nextQuestion = () => {
    if (currentIndex + 1 < questions.length) {
      setCurrentIndex((prev) => prev + 1);
      setSelectedAnswer(null);
    } else {
      endGame();
    }
  };

  const getButtonClass = (answer) => {
    if (!selectedAnswer) return "";
    if (answer === currentQuestion.correctAnswer) return "correct";
    if (answer === selectedAnswer) return "wrong";
    return "";
  };


  // ── Enregistrement session ────────────────────────────────────────
  const saveSession = async (timeSeconds) => {
    try {
      await api.post('/api/gameplay/session/', {
        game_code:    'mcq',
        theme:        theme,
        sous_theme:   subTheme,
        level:        level,
        score:        score,
        total:        questions.length,
        correct:      score,
        wrong:        questions.length - score,
        time_seconds: timeSeconds || timeElapsed,
      });
    } catch (e) {
      console.warn('Session non enregistree :', e);
    }
  };

  const endGame = async () => {
    const t = getTimerTimeRef.current ? getTimerTimeRef.current() : timeElapsed;
    sendTime(t);
    await saveSession(t);
    setFinished(true);
    setTimerIsFinished(true);
  };

  return (
    <div className="page-container">
      <Sidebar />

    {loading ? (
            <div className="content-container inGame" id="vinyleQCM">
              <div className="loading-container">
                  <h1>Chargement...</h1>
              </div>
            </div>
          ) : apiError ? (
            <div className="content-container inGame" id="vinyleQCM">
              <div className="error-container">
                  <h2>Une erreur est survenue lors du chargement des données.</h2>
                  <button className="mode-btn active" onClick={() => window.location.reload()}>
                      Réessayer
                  </button>
              </div>
            </div>
          ) : (
            <GameWrapper onStart={() => setGameStarted(true)}>
              <div className="content-container inGame" id="vinyleQCM">
                <div className="title">
                  <h4>{theme} - {subTheme}</h4>
                  <div className="level-infos">
                    <Timer
                      timer={timerIsFinished}
                      onFinish={() => endGame()}
                      onSendTime={sendTime}
                      onGetTime={(getTimeFn) => (getTimerTimeRef.current = getTimeFn)}
                      hasLimit={true}
                      limit={120}
                    />
                  </div>
                </div>

                <div className="game-container launched">
                  {!finished && currentQuestion ? (
                    <>
                      <div className="vinyle-info">
                        <div className="vinyle-length">
                          <p>Question {currentIndex + 1}/{questions.length}</p>
                          <div className="progress-bar">
                            <div
                              className="progress-fill"
                              style={{ width: `${((currentIndex + 1) / questions.length) * 100}%` }}
                            ></div>
                          </div>
                        </div>
                        <h3 className="vinyle-score">Score : {score}/{questions.length}</h3>
                      </div>
                      <div className="vinyle-QR">
                        <h2 className="vinyle-question">{currentQuestion.question}</h2>
                        <h3 className="vinyle-rules">Cliquez sur une réponse pour la valider.</h3>
                      </div>
                      <div className="vinyle-options">
                        {currentQuestion.options.map((answer, index) => (
                          <button
                            key={index}
                            className={`vinyle-button ${getButtonClass(answer)}`}
                            onClick={() => handleAnswerClick(answer)}
                            disabled={!!selectedAnswer}
                            onMouseEnter={() => setHoveredAnswer(answer)}
                            onMouseLeave={() => setHoveredAnswer(null)}
                          >
                            {hoveredAnswer === answer && translations[answer]
                              ? translations[answer]
                              : answer}
                          </button>
                        ))}
                      </div>
                      {selectedAnswer && (
                        <button className="vinyle-next" onClick={nextQuestion}>
                          {currentIndex + 1 < questions.length ? "Question suivante" : "Terminer"}
                        </button>
                      )}
                    </>
                  ) : (
                    <h3 className="vinyle-score">Score final : {score}/{questions.length}</h3>
                  )}
                </div>
              </div>
            </GameWrapper>
      )}

      <GameMenu onSendLeaveGame={sendLeaveGame} />
      <Popup time={timeElapsed} timerIsFinished={timerIsFinished} errorCount={questions.length - score} />
      <LeaveGamePopup popupOpen={leaveGamePopup} onSendLeaveGame={sendLeaveGame} />
    </div>
  );
}